// dependencies
import { UseQueryResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { GetBaseSearchServiceResponse, InternalApiSearchServiceOptions } from "@performyard/service-contracts";

import { searchServiceQuery } from "../../queries";
// api
import { SearchServiceResponse, getSearch } from "../../services/defs/search-service";

export type SortDirection = "asc" | "desc";

export type SearchServiceSort = {
	name: string;
	direction: SortDirection;
};

export const SearchServiceSortSchema = z.object({
	name: z.string(),
	direction: z.enum(["asc", "desc"]),
});

type SearchServiceOptions = {
	service: string;
	filters?: any;
	skip?: number;
	limit?: number;
	sort?: SearchServiceSort;
	defaultErrorMessage?: string;
	disabled?: boolean;
	disableError?: boolean;
};

const calculateSearchServiceQuery = <T>({
	service,
	filters,
	skip = 0,
	limit = 25,
	sort,
	defaultErrorMessage = "There was an error getting search results.",
	disabled = false,
	disableError = false,
}: SearchServiceOptions) => ({
	queryKey: ["search-service", service, JSON.stringify(sort), JSON.stringify(filters), skip, limit, disabled],
	queryFn: async () => await getSearch<T>(service, filters, skip, limit, sort),
	enabled: !disabled,
	meta: {
		errorMessage: defaultErrorMessage,
		disableDefaultErrorHandling: disableError,
	},
});

export const useSearchService = <T>(props: SearchServiceOptions) => {
	const query = useQuery<SearchServiceResponse<T>>({
		...calculateSearchServiceQuery<T>(props),
	});

	return query;
};

export const useSearchServicePrefetch = <T>(props: SearchServiceOptions, numberOfPages = 1) => {
	const queryClient = useQueryClient();
	if (!props.disabled && props.skip) {
		for (let i = 0; i < numberOfPages; i++) {
			const skip = props.skip + i * (props.limit || 25);
			if (skip >= 0) {
				const currentProps = { ...props, skip, disableError: true };
				queryClient.prefetchQuery({ ...calculateSearchServiceQuery<T>(currentProps) });
			}
		}
	}
};

export const useInternalApiSearchService = (
	props: InternalApiSearchServiceOptions
): UseQueryResult<GetBaseSearchServiceResponse> => {
	const query = useQuery<GetBaseSearchServiceResponse>(searchServiceQuery(props));
	return query;
};

export const useInternalApiSearchServicePrefetch = (
	props: InternalApiSearchServiceOptions,
	numberOfPages = 1
): void => {
	const { disabled = false, limit = 25, skip = 0 } = props;
	const queryClient = useQueryClient();

	// get total from current query data
	const currentQueryData = queryClient.getQueryData<GetBaseSearchServiceResponse>(searchServiceQuery(props).queryKey);
	const total = currentQueryData?.total || 0;

	// prefetch the next pages
	if (!disabled) {
		// Prefetch pages ahead (next pages)
		for (let i = 1; i <= numberOfPages; i++) {
			const nextSkip = skip + limit * i;
			// Only prefetch if the page exists (skip is within total bounds)
			if (nextSkip < total) {
				const currentProps = { ...props, skip: nextSkip };
				queryClient.prefetchQuery(searchServiceQuery(currentProps));
			}
		}

		// Prefetch pages behind (previous pages)
		for (let i = 1; i <= numberOfPages; i++) {
			const prevSkip = skip - limit * i;
			// Only prefetch if the page exists (skip is non-negative)
			if (prevSkip >= 0) {
				const currentProps = { ...props, skip: prevSkip };
				queryClient.prefetchQuery(searchServiceQuery(currentProps));
			}
		}
	}
};
